// Mapa.js
import { useRef, useEffect, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import Input from "@mui/joy/Input";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";

mapboxgl.accessToken = process.env.MAPBOX_TOKEN;

export default function Mapa({ onLocationSelect, mapStyle }) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!mapRef.current && mapContainerRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: "mapbox://styles/mapbox/streets-v11",
        center: [0, 0],
        zoom: 1.5,
        projection: "globe",
      });

      mapRef.current.on("style.load", () => {
        mapRef.current.setFog({});
      });

      // Maneja el evento de clic en el mapa
      mapRef.current.on("click", async (e) => {
        const { lng, lat } = e.lngLat;

        try {
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`
          );
          const data = await response.json();
          if (data.features && data.features.length > 0) {
            const placeName = data.features[0].place_name;

            // Actualiza el input con la dirección obtenida y envía la información al prop
            setSearch(placeName);
            onLocationSelect({ address: placeName, coordinates: { lng, lat } });
          }
        } catch (error) {
          console.error("Error al obtener la dirección:", error);
        }
      });
    }
  }, [onLocationSelect]);

  const handleSearchChange = async (e) => {
    const query = e.target.value;
    setSearch(query);

    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${mapboxgl.accessToken}&autocomplete=true&limit=5`
      );
      const data = await response.json();
      setSuggestions(data.features || []);
    } catch (error) {
      console.error("Error al obtener sugerencias:", error);
    }
  };

  const handleSuggestionClick = (place) => {
    const [longitude, latitude] = place.geometry.coordinates;

    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: 14,
        essential: true,
      });
    }

    setSearch(place.place_name);
    setSuggestions([]);
    onLocationSelect({
      address: place.place_name,
      coordinates: { lng: longitude, lat: latitude },
    });
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <Input
        placeholder="Buscar ubicación..."
        value={search}
        onChange={handleSearchChange}
        fullWidth
        sx={{ marginBottom: "10px" }}
      />

      {suggestions.length > 0 && (
        <List
          sx={{
            position: "absolute",
            top: "40px",
            width: "100%",
            backgroundColor: "white",
            maxHeight: "200px",
            overflowY: "auto",
            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
            zIndex: 2,
          }}
        >
          {suggestions.map((place) => (
            <ListItem
              key={place.id}
              onClick={() => handleSuggestionClick(place)}
              sx={{ cursor: "pointer", padding: "8px" }}
            >
              {place.place_name}
            </ListItem>
          ))}
        </List>
      )}
      <div
        ref={mapContainerRef}
        style={{
          width: mapStyle?.width || "100%",
          height: mapStyle?.height || "50vh",
          borderRadius: "12px",
        }}
      />
    </div>
  );
}
